import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Space, 
  Row, 
  Col, 
  Modal, 
  Typography, 
  Tag, 
  Badge,
  Alert,
  Empty,
  Popconfirm,
  Progress,
  Spin,
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  KeyOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  EditOutlined,
  ThunderboltOutlined,
  LoadingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { settingsApi, DomainCheckJob } from '../services/settingsApi';
import { useQuery, useMutation, useQueryClient } from 'react-query';

const { Title, Text, Paragraph } = Typography;

interface SettingItem {
  id: string;
  key: string;
  value: string;
  description: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  isActive: boolean;
}

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<SettingItem[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState<{[key: string]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [domainCheckJobId, setDomainCheckJobId] = useState<string | null>(null);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmEnemyDomainsModalVisible, setConfirmEnemyDomainsModalVisible] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const allSettings = await settingsApi.getAllSettings();
      
      // Convert API settings to SettingItem format
      const convertedSettings: SettingItem[] = allSettings.map((setting, index) => ({
        id: setting._id || `setting_${index}`,
        key: setting.key,
        value: setting.value,
        description: setting.description || setting.key,
        type: setting.type || 'string',
        isActive: setting.isActive !== false
      }));
      
      setSettings(convertedSettings);
      
      // Convert settings array to form values
      const formValues: any = {};
      convertedSettings.forEach(setting => {
        formValues[setting.key] = setting.value;
      });
      form.setFieldsValue(formValues);
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      // Validate settings before saving
      const validSettings = settings.filter(setting => setting.key.trim());
      const incompleteSettings = settings.filter(setting => 
        !setting.key.trim() || !setting.description.trim() || !setting.value.trim()
      );
      
      if (incompleteSettings.length > 0) {
        message.error('Please fill in all fields (Label, Key, Value) for all settings');
        return;
      }
      
      // Check for duplicate keys
      const keys = validSettings.map(s => s.key);
      const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
      
      if (duplicateKeys.length > 0) {
        message.error(`Duplicate keys found: ${duplicateKeys.join(', ')}`);
        return;
      }
      
      // Process all valid settings
      const savePromises = validSettings.map(async (setting) => {
        return settingsApi.setSetting(
          setting.key, 
          setting.value, 
          setting.description, 
          setting.type
        );
      });
      
      await Promise.all(savePromises);
      await loadSettings(); // Reload settings
      setHasChanges(false);
      message.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInitSettings = async () => {
    try {
      setLoading(true);
      await settingsApi.initializeDefaultSettings();
      message.success('Settings initialized successfully!');
      // Reload settings after initialization
      await loadSettings();
    } catch (error) {
      console.error('Error initializing settings:', error);
      message.error('Failed to initialize settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSetting = () => {
    const newSetting: SettingItem = {
      id: Date.now().toString(),
      key: '',
      value: '',
      description: '',
      type: 'string',
      isActive: true
    };
    
    setSettings([...settings, newSetting]);
    form.setFieldsValue({ [newSetting.id]: newSetting.value });
  };

  const handleRemoveSetting = (settingId: string) => {
    setSettingToDelete(settingId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!settingToDelete) return;
    
    try {
      // Find the setting to delete
      const setting = settings.find(s => s.id === settingToDelete);
      if (setting && setting.key) {
        // If it's an existing setting (has key), delete from backend
        await settingsApi.deleteSetting(setting.key);
      }
      
      // Remove from local state
      setSettings(settings.filter(s => s.id !== settingToDelete));
      
      message.success('Setting deleted successfully!');
    } catch (error) {
      console.error('Error deleting setting:', error);
      message.error('Failed to delete setting');
    } finally {
      setDeleteModalVisible(false);
      setSettingToDelete(null);
    }
  };

  const handleSettingChange = (settingId: string, field: string, value: string) => {
    setSettings(settings.map(setting => 
      setting.id === settingId 
        ? { ...setting, [field]: value }
        : setting
    ));
    setHasChanges(true);
  };

  const togglePasswordVisibility = (settingId: string) => {
    setPasswordVisible(prev => ({
      ...prev,
      [settingId]: !prev[settingId]
    }));
  };

  const getSettingTypeColor = (type: string) => {
    switch (type) {
      case 'string': return 'blue';
      case 'number': return 'green';
      case 'boolean': return 'orange';
      case 'json': return 'purple';
      default: return 'default';
    }
  };

  const isPasswordField = (key: string) => {
    return key.includes('api') || key.includes('key') || key.includes('password') || key.includes('secret');
  };

  // Domain check job mutation
  const startDomainCheckMutation = useMutation(
    () => settingsApi.startDomainCheck(),
    {
      onSuccess: (data) => {
        setDomainCheckJobId(data.jobId);
        setProgressModalVisible(true);
        message.success('Domain check job started!');
      },
      onError: (error: any) => {
        message.error(`Failed to start domain check: ${error.message}`);
      },
    }
  );

  // Enemy domains died mutation
  const checkEnemyDomainsDiedMutation = useMutation(
    () => settingsApi.checkEnemyDomainsDied(),
    {
      onSuccess: (data) => {
        const { processingResult, data: domains } = data;
        if (processingResult) {
          const { processed, spamLinksUpdated, redirectRulesDeleted, errors } = processingResult;
          message.success(
            `Check completed! Processed: ${processed}, Spam Links Updated: ${spamLinksUpdated}, ` +
            `Redirect Rules Deleted: ${redirectRulesDeleted}${errors.length > 0 ? `, Errors: ${errors.length}` : ''}`
          );
        } else if (domains && domains.length === 0) {
          message.info('No died domains found');
        } else {
          message.success('Check completed successfully!');
        }
      },
      onError: (error: any) => {
        message.error(`Failed to check enemy domains died: ${error.message || 'Unknown error'}`);
      },
    }
  );

  // Poll job status
  const { data: jobStatus } = useQuery(
    ['domainCheckJob', domainCheckJobId],
    () => settingsApi.getDomainCheckStatus(domainCheckJobId!),
    {
      enabled: !!domainCheckJobId && progressModalVisible,
      refetchInterval: (data) => {
        const job = data as DomainCheckJob | null;
        if (job?.status === 'completed' || job?.status === 'failed' || job?.status === 'stopped') {
          return false; // Stop polling when completed, failed, or stopped
        }
        return 2000; // Poll every 2 seconds
      },
      onSuccess: (data) => {
        if (data?.status === 'completed') {
          message.success('Domain check completed successfully!');
        } else if (data?.status === 'failed') {
          message.error('Domain check failed!');
        } else if (data?.status === 'stopped') {
          message.warning('Domain check job was stopped.');
        }
      },
    }
  );

  // Stop job mutation
  const stopDomainCheckMutation = useMutation(
    (jobId: string) => settingsApi.stopDomainCheck(jobId),
    {
      onSuccess: (data) => {
        if (data.success) {
          message.success('Job stop request sent successfully');
          // Refetch job status to update UI
          queryClient.invalidateQueries(['domainCheckJob', domainCheckJobId]);
        } else {
          message.error(data.message || 'Failed to stop job');
        }
      },
      onError: (error: any) => {
        message.error(`Failed to stop job: ${error.message}`);
      },
    }
  );

  const handleStopJob = () => {
    if (!domainCheckJobId) return;
    
    Modal.confirm({
      title: 'Stop Domain Check Job',
      content: 'Are you sure you want to stop this job? The job will stop after completing the current batch.',
      okText: 'Yes, Stop Job',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: () => {
        stopDomainCheckMutation.mutate(domainCheckJobId);
      },
    });
  };

  const handleStartDomainCheck = () => {
    setConfirmModalVisible(true);
  };

  const handleConfirmDomainCheck = () => {
    setConfirmModalVisible(false);
    startDomainCheckMutation.mutate();
  };

  const handleCloseProgressModal = () => {
    if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
      setProgressModalVisible(false);
      setDomainCheckJobId(null);
    } else {
      message.warning('Please wait for the job to complete');
    }
  };

  const getProgressPercentage = (): number => {
    if (!jobStatus || jobStatus.totalDomains === 0) return 0;
    const completed = jobStatus.completedDomains + jobStatus.failedDomains;
    return Math.round((completed / jobStatus.totalDomains) * 100);
  };

  return (
    <div>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header Section */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <Title level={1} style={{ margin: 0, fontSize: '2.5rem' }}>
            <SettingOutlined style={{ marginRight: '16px' }} />
            Settings Management
          </Title>
          <Paragraph style={{ fontSize: '1.1rem', margin: '8px 0 0 0' }}>
            Configure and manage your application settings with ease
          </Paragraph>
        </div>

        {/* Generate Settings Panel */}
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ThunderboltOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
              <span style={{ fontSize: '18px', fontWeight: 600 }}>Generate Settings</span>
            </div>
          }
          style={{ 
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none',
            marginBottom: '24px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: '32px' } }}
        >
          {/* Manual Check Domains Indexed */}
          <Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Text strong style={{ fontSize: '16px' }}>
                Manual Check Domains Indexed
              </Text>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Button 
                type="primary"
                size="large"
                style={{ borderRadius: '8px' }}
                onClick={handleStartDomainCheck}
                loading={startDomainCheckMutation.isLoading}
                icon={<ThunderboltOutlined />}
              >
                Check Now
              </Button>
            </Col>
          </Row>

          {/* Manual Check Domains Spam Link died */}
          <Row gutter={[16, 16]} align="middle">
            <Col span={12}>
              <Text strong style={{ fontSize: '16px' }}>
                Manual Check Domains Spam Link died
              </Text>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Button 
                type="primary"
                size="large"
                style={{ borderRadius: '8px' }}
                onClick={() => setConfirmEnemyDomainsModalVisible(true)}
                loading={checkEnemyDomainsDiedMutation.isLoading}
                icon={<ThunderboltOutlined />}
              >
                Check Now
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Main Settings Card */}
        <Card 
          style={{ 
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: '32px' } }}
        >
          {/* Action Bar */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Badge count={settings.length} style={{ backgroundColor: '#52c41a' }}>
                <Tag color="blue" icon={<SettingOutlined />}>
                  Total Settings
                </Tag>
              </Badge>
              {hasChanges && (
                <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                  Unsaved Changes
                </Tag>
              )}
            </div>
            
            <Space>
              <Popconfirm
                title="Initialize Default Settings"
                description="This will create/update default settings for the application. Existing settings with the same keys will be updated. Continue?"
                onConfirm={handleInitSettings}
                okText="Yes, Initialize"
                cancelText="Cancel"
                okButtonProps={{ type: 'primary' }}
              >
                <Button 
                  type="default"
                  icon={<ThunderboltOutlined />}
                  loading={loading}
                  size="large"
                  style={{ borderRadius: '8px' }}
                >
                  Init Settings
                </Button>
              </Popconfirm>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleAddSetting}
                size="large"
              >
                Add New Setting
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadSettings}
                loading={loading}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Refresh
              </Button>
            </Space>
          </div>

          {/* Settings List */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            size="large"
          >
            {settings.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No settings configured yet"
                style={{ padding: '60px 0' }}
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSetting}>
                  Add Your First Setting
                </Button>
              </Empty>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {settings.map((setting, index) => (
                  <Card 
                    key={setting.id} 
                    style={{ 
                      borderRadius: '12px',
                      border: '1px solid #e8e8e8',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease',
                    }}
                    styles={{ body: { padding: '20px' } }}
                  >
                    <Row gutter={[16, 16]} align="middle">
                      {/* Setting Number */}
                      <Col span={1}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}>
                          {index + 1}
                        </div>
                      </Col>

                      {/* Label Input */}
                      <Col span={6}>
                        <div>
                          <Text strong style={{ color: '#1890ff', fontSize: '12px' }}>LABEL</Text>
                          <Input
                            placeholder="Setting description"
                            value={setting.description}
                            onChange={(e) => handleSettingChange(setting.id, 'description', e.target.value)}
                            style={{ 
                              marginTop: '4px', 
                              borderRadius: '8px',
                              borderColor: !setting.description.trim() ? '#ff4d4f' : undefined
                            }}
                            prefix={<EditOutlined style={{ color: '#1890ff' }} />}
                          />
                        </div>
                      </Col>

                      {/* Key Input */}
                      <Col span={5}>
                        <div>
                          <Text strong style={{ color: '#52c41a', fontSize: '12px' }}>KEY</Text>
                          <Input
                            placeholder="Unique key"
                            value={setting.key}
                            onChange={(e) => handleSettingChange(setting.id, 'key', e.target.value)}
                            style={{ 
                              marginTop: '4px', 
                              borderRadius: '8px',
                              borderColor: !setting.key.trim() ? '#ff4d4f' : undefined
                            }}
                            prefix={<KeyOutlined style={{ color: '#52c41a' }} />}
                          />
                        </div>
                      </Col>

                      {/* Value Input */}
                      <Col span={10}>
                        <div>
                          <Text strong style={{ color: '#722ed1', fontSize: '12px' }}>VALUE</Text>
                          <div style={{ position: 'relative', marginTop: '4px' }}>
                            {isPasswordField(setting.key) ? (
                              <Input.Password
                                placeholder="Enter value"
                                value={setting.value}
                                onChange={(e) => handleSettingChange(setting.id, 'value', e.target.value)}
                                style={{ 
                                  borderRadius: '8px', 
                                  paddingRight: '40px',
                                  borderColor: !setting.value.trim() ? '#ff4d4f' : undefined
                                }}
                                visibilityToggle={{
                                  visible: passwordVisible[setting.id],
                                  onVisibleChange: () => togglePasswordVisibility(setting.id)
                                }}
                              />
                            ) : (
                              <Input
                                placeholder="Enter value"
                                value={setting.value}
                                onChange={(e) => handleSettingChange(setting.id, 'value', e.target.value)}
                                style={{ 
                                  borderRadius: '8px',
                                  borderColor: !setting.value.trim() ? '#ff4d4f' : undefined
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </Col>

                      {/* Type Tag */}
                      <Col span={1}>
                        <Tag color={getSettingTypeColor(setting.type)} style={{ borderRadius: '6px' }}>
                          {setting.type.toUpperCase()}
                        </Tag>
                      </Col>

                      {/* Actions */}
                      <Col span={1}>
                        <Button 
                          type="text" 
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveSetting(setting.id)}
                          style={{ 
                            borderRadius: '8px',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Save Actions */}
            {settings.length > 0 && (
              <div style={{ 
                marginTop: '32px', 
              }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                      <Text strong style={{ fontSize: '16px' }}>
                        {settings.length} setting{settings.length !== 1 ? 's' : ''} configured
                      </Text>
                      {hasChanges && (
                        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                          You have unsaved changes
                        </Tag>
                      )}
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        icon={<SaveOutlined />}
                        loading={loading}
                        size="large"
                        disabled={!hasChanges}
                        style={{ 
                          borderRadius: '8px',
                          background: hasChanges ? 'linear-gradient(45deg, #52c41a, #73d13d)' : undefined,
                          border: 'none',
                          boxShadow: hasChanges ? '0 4px 12px rgba(82, 196, 26, 0.4)' : undefined
                        }}
                      >
                        Save All Changes
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </div>
            )}
          </Form>
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              Confirm Delete Setting
            </div>
          }
          open={deleteModalVisible}
          onOk={confirmDelete}
          onCancel={() => setDeleteModalVisible(false)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ 
            danger: true,
            style: { borderRadius: '8px' }
          }}
          cancelButtonProps={{ style: { borderRadius: '8px' } }}
          style={{ borderRadius: '12px' }}
        >
          <Alert
            message="Warning"
            description="Are you sure you want to delete this setting? This action cannot be undone and may affect your application functionality."
            type="warning"
            showIcon
            style={{ borderRadius: '8px' }}
          />
        </Modal>

        {/* Domain Check Confirm Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ThunderboltOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
              Confirm Domain Index Check
            </div>
          }
          open={confirmModalVisible}
          onOk={handleConfirmDomainCheck}
          onCancel={() => setConfirmModalVisible(false)}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{ 
            type: 'primary',
            style: { borderRadius: '8px' },
            loading: startDomainCheckMutation.isLoading
          }}
          cancelButtonProps={{ style: { borderRadius: '8px' } }}
          style={{ borderRadius: '12px' }}
        >
          <Alert
            message="Domain Index Check"
            description="This will start checking index status for all active domains. This process may take several minutes. Do you want to continue?"
            type="info"
            showIcon
            style={{ borderRadius: '8px' }}
          />
        </Modal>

        {/* Enemy Domains Died Confirmation Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined style={{ color: '#ff9800' }} />
              Confirm Check Enemy Domains Died
            </div>
          }
          open={confirmEnemyDomainsModalVisible}
          onOk={() => {
            setConfirmEnemyDomainsModalVisible(false);
            checkEnemyDomainsDiedMutation.mutate();
          }}
          onCancel={() => setConfirmEnemyDomainsModalVisible(false)}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{ 
            type: 'primary',
            style: { borderRadius: '8px' },
            loading: checkEnemyDomainsDiedMutation.isLoading
          }}
          cancelButtonProps={{ style: { borderRadius: '8px' } }}
          style={{ borderRadius: '12px' }}
        >
          <Alert
            message="Check Enemy Domains Died"
            description="This will check for died domains from AIO API and process them (remove redirect rules, update spam links, mark history as recovered). This process may take several minutes. Do you want to continue?"
            type="warning"
            showIcon
            style={{ borderRadius: '8px' }}
          />
        </Modal>

        {/* Domain Check Progress Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ThunderboltOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
              Domain Index Check Progress
            </div>
          }
          open={progressModalVisible}
          onCancel={handleCloseProgressModal}
          footer={[
            jobStatus?.status === 'processing' && (
              <Button 
                key="stop"
                danger
                icon={<StopOutlined />}
                onClick={handleStopJob}
                loading={stopDomainCheckMutation.isLoading}
                style={{ borderRadius: '8px' }}
              >
                Stop Job
              </Button>
            ),
            <Button 
              key="close"
              onClick={handleCloseProgressModal}
              disabled={jobStatus?.status === 'processing'}
              style={{ borderRadius: '8px' }}
            >
              {jobStatus?.status === 'completed' || jobStatus?.status === 'failed' || jobStatus?.status === 'stopped' ? 'Close' : 'Cancel'}
            </Button>
          ]}
          closable={jobStatus?.status === 'completed' || jobStatus?.status === 'failed' || jobStatus?.status === 'stopped'}
          maskClosable={false}
          style={{ borderRadius: '12px' }}
          width={600}
        >
          {!jobStatus ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>Starting domain check job...</Text>
              </div>
            </div>
          ) : (
            <div style={{ padding: '20px 0' }}>
              {/* Status */}
              <div style={{ marginBottom: '24px' }}>
                <Space>
                  <Tag 
                    color={
                      jobStatus.status === 'completed' ? 'green' :
                      jobStatus.status === 'failed' ? 'red' :
                      jobStatus.status === 'stopped' ? 'orange' :
                      jobStatus.status === 'processing' ? 'blue' : 'default'
                    }
                    icon={
                      jobStatus.status === 'processing' ? <LoadingOutlined spin /> :
                      jobStatus.status === 'completed' ? <CheckCircleOutlined /> :
                      jobStatus.status === 'stopped' ? <StopOutlined /> :
                      <ExclamationCircleOutlined />
                    }
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {jobStatus.status.toUpperCase()}
                  </Tag>
                  {jobStatus.jobId && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Job ID: {jobStatus.jobId}
                    </Text>
                  )}
                </Space>
              </div>

              {/* Progress Bar */}
              <div style={{ marginBottom: '24px' }}>
                <Progress
                  percent={getProgressPercentage()}
                  status={
                    jobStatus.status === 'failed' ? 'exception' :
                    jobStatus.status === 'stopped' ? 'exception' :
                    jobStatus.status === 'completed' ? 'success' : 'active'
                  }
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>

              {/* Statistics */}
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {jobStatus.totalDomains}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Total Domains
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {jobStatus.completedDomains}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Completed
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {jobStatus.failedDomains}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Failed
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Errors */}
              {jobStatus.errors && jobStatus.errors.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <Alert
                    message="Errors"
                    description={
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {jobStatus.errors.slice(0, 5).map((error, index) => (
                          <div key={index} style={{ marginBottom: '8px', fontSize: '12px' }}>
                            <Text strong>{error.domain}:</Text> <Text type="danger">{error.error}</Text>
                          </div>
                        ))}
                        {jobStatus.errors.length > 5 && (
                          <Text type="secondary">... and {jobStatus.errors.length - 5} more errors</Text>
                        )}
                      </div>
                    }
                    type="error"
                    showIcon
                    style={{ borderRadius: '8px' }}
                  />
                </div>
              )}

              {/* Time Info */}
              {jobStatus.startedAt && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Started: {new Date(jobStatus.startedAt).toLocaleString()}
                  </Text>
                  {jobStatus.completedAt && (
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: '16px' }}>
                      Completed: {new Date(jobStatus.completedAt).toLocaleString()}
                    </Text>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Settings;
